import { useLocal } from "hooks/useLocal";
import { Link } from "react-router-dom";

import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";

import logo from "../../../assets/images/logo.svg";
import { detectDevice, launchAppOrFallback } from "utils";
import { APP_STORE_URL } from "constants/index";

type FooterV2Props = {
  onPrimaryCtaClick?: () => void;
};

export default function FooterV2({ onPrimaryCtaClick }: FooterV2Props) {
  const { direction, t, isArabic } = useLocal();

  const scrollToDownloadSectionCenter = () => {
    const downloadSection = document.querySelector(
      '[data-journey-download-section="true"]',
    );

    if (downloadSection instanceof HTMLElement) {
      downloadSection.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    window.scrollTo({
      top: window.scrollY + window.innerHeight / 2,
      behavior: "smooth",
    });
  };

  const handleDownloadClick = () => {
    if (onPrimaryCtaClick) {
      onPrimaryCtaClick();
      return;
    }

    const { platform } = detectDevice();

    if (platform === "ios") {
      launchAppOrFallback({
        deepLink: "numuw://",
        fallbackUrl: APP_STORE_URL,
      });
      return;
    }

    if (platform === "android") {
      launchAppOrFallback({
        deepLink: "numuw://",
        fallbackUrl: APP_STORE_URL,
      });
      return;
    }

    scrollToDownloadSectionCenter();
  };

  const lan = isArabic ? "ar" : "en";
  const lanWithParam = isArabic ? "?lang=ar" : "?lang=en";

  const socialParam = isArabic ? "?locale=ar_AR" : "?locale=en_US";

  const companyLinks = [
    {
      label: t("footerV2.company.links.joinNetwork"),
      href: "/join-our-network",
    },
    // {
    //   label: t("footerV2.company.links.courses"),
    //   href: "/courses",
    // },
    // {
    //   label: t("footerV2.company.links.forSchools"),
    //   href: "/school",
    // },
    {
      label: t("footerV2.company.links.resourcesHub"),
      href: "https://resources.numuw.com/",
    },
  ];

  const supportLinks = [
    // {
    //   label: t("footerV2.support.links.helpCenter"),
    //   href: "https://intercom.help/numuw-telehealth/" + lan,
    // },
    {
      label: t("footerV2.support.links.contactUs"),
      href: "/contact-us" + lanWithParam,
    },
    {
      label: t("footerV2.support.links.privacyPolicy"),
      href: "/privacy-policy" + lanWithParam,
      newTab: true,
    },
    {
      label: t("footerV2.support.links.termsOfService"),
      href: "/terms-of-use" + lanWithParam,
      newTab: true,
    },
  ];

  const socialLinks = [
    {
      icon: <InstagramIcon sx={{ fontSize: 18 }} />,
      href: "https://www.instagram.com/numuwapp/" + socialParam,
      label: t("footerV2.social.instagram"),
    },
    {
      icon: <FacebookIcon sx={{ fontSize: 18 }} />,
      href: "https://www.facebook.com/numuwhub/" + socialParam,
      label: t("footerV2.social.facebook"),
    },
    {
      icon: <LinkedInIcon sx={{ fontSize: 18 }} />,
      href: "https://www.linkedin.com/company/numuw/" + lan,
      label: t("footerV2.social.linkedin"),
    },
    {
      icon: <YouTubeIcon sx={{ fontSize: 18 }} />,
      href: "https://www.youtube.com/@numuwhub1896",
      label: t("footerV2.social.youtube"),
    },
  ];

  const dirAttr: string | undefined = Array.isArray(direction)
    ? direction[0]
    : direction;

  return (
    <footer
      dir={dirAttr}
      className={`bg-[#F4F3FC] ${isArabic ? "font-ibmplex" : "font-inter"}`}
    >
      <article className="py-16 max-w-[1450px] mx-auto w-full">
        <section className="flex flex-col md:flex-row md:items-start md:justify-between gap-10 md:gap-6">
          <article className="flex flex-col gap-5 max-w-xs">
            <section className="flex items-start justify-between md:block">
              <a href="/">
                <img src={logo} width={77} alt="numuw logo" />
              </a>

              <button
                type="button"
                className="md:hidden bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 whitespace-nowrap"
                onClick={handleDownloadClick}
              >
                {t("footerV2.download")}
              </button>
            </section>

            <p className="text-sm text-[#515151]">
              {t("footerV2.description")}
            </p>

            {/* Social icons */}
            <article className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-[#E5E3F7] flex items-center justify-center text-indigo-500 hover:bg-indigo-200 transition-colors duration-200"
                >
                  {social.icon}
                </a>
              ))}
            </article>
          </article>
          <section className="flex gap-10 md:gap-24">
            {/* Company */}
            <article className="flex flex-col gap-4">
              <p className="text-sm font-bold text-indigo-600">
                {t("footerV2.company.title")}
              </p>
              <ul className="flex flex-col gap-[14px]">
                {companyLinks.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("http") ? (
                      <a
                        href={link.href + lanWithParam}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#515151]"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href + lanWithParam}
                        className="text-sm text-[#515151]"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </article>

            {/* Support */}
            <article className="flex flex-col gap-4">
              <p className="text-sm font-bold text-indigo-600">
                {t("footerV2.support.title")}
              </p>
              <ul className="flex flex-col gap-[14px]">
                {supportLinks.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("http") || link.newTab ? (
                      <a
                        href={link.href}
                        className="text-sm text-[#515151]"
                        target={link.newTab ? "_blank" : undefined}
                        rel="noopener noreferrer"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm text-[#515151]">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </article>
          </section>

          {/* Right: Download App — desktop only */}
          <article className="hidden md:flex items-start">
            <button
              type="button"
              className="bg-[#4242CE] hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-3 rounded-xl"
              onClick={handleDownloadClick}
            >
              {t("footerV2.download")}
            </button>
          </article>
        </section>

        {/* Divider */}
        <div className="mt-12 border-t border-solid border-gray-300 h-[10px] w-full" />

        {/* copyright */}
        <p
          className={`mt-6 text-sm ${direction === "rtl" ? "text-right" : "text-left"} text-[#515151]`}
        >
          {t("footerV2.copyright")}
        </p>
      </article>
    </footer>
  );
}
